import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import {
  FABRIC_AVOID_OPTIONS,
  HERITAGE_OPTIONS,
  HeightInput,
  MODESTY_OPTIONS,
  RELIGIOUS_EVENTS_OPTIONS,
  SIZE_SYSTEMS,
  WeightInput,
} from './quiz/quizInputs';

// ============ CORE REQUIRED STEPS ============
const CORE_STEPS = [
  {
    id: 'gender',
    title: 'Identity',
    question: 'How do you identify when it comes to style?',
    explanation: 'This helps us tailor silhouettes and cuts that feel authentic to how you dress. Your answer guides our suggestions, not your identity.',
    type: 'single',
    options: [
      { value: 'female', label: 'Female', desc: 'Womenswear-led recommendations' },
      { value: 'male', label: 'Male', desc: 'Menswear-led recommendations' },
      { value: 'both', label: 'Both', desc: 'A curated mix across categories' },
      { value: 'refuse', label: 'Prefer not to say', desc: 'Gender-neutral recommendations' }
    ]
  },
  {
    id: 'units',
    title: 'Measurements',
    question: 'Which units feel most natural to you?',
    explanation: 'We\'ll show your height and weight in your preferred system. You can change this later in your profile.',
    type: 'single',
    options: [
      { value: 'imperial', label: 'Imperial', desc: 'Feet, inches, pounds' },
      { value: 'metric', label: 'Metric', desc: 'Centimetres, kilograms' }
    ]
  },
  {
    id: 'skin_tone',
    title: 'Skin Tone',
    question: 'How would you describe your skin tone?',
    explanation: 'Your skin tone is the surface depth of your complexion. We pair it with your undertone to recommend colours that bring out your natural radiance.',
    type: 'single',
    options: [
      { value: 'fair', label: 'Fair', desc: 'Very light, often with visible veins' },
      { value: 'light', label: 'Light', desc: 'Light but not pale' },
      { value: 'medium', label: 'Medium', desc: 'Neutral middle range' },
      { value: 'olive', label: 'Olive', desc: 'Light to medium with green/yellow' },
      { value: 'tan', label: 'Tan', desc: 'Warm bronze or honey tones' },
      { value: 'deep', label: 'Deep', desc: 'Rich, deeper complexion' }
    ]
  },
  {
    id: 'undertone',
    title: 'Undertone',
    question: 'What is your skin undertone?',
    explanation: 'Undertone is the hue beneath your skin\'s surface. Knowing this is the single biggest factor in choosing colours that make your complexion glow.',
    type: 'single',
    options: [
      { value: 'warm', label: 'Warm', desc: 'Golden, yellow, or peachy hues' },
      { value: 'cool', label: 'Cool', desc: 'Pink, red, or bluish hues' },
      { value: 'neutral', label: 'Neutral', desc: 'A balanced mix of both' }
    ]
  },
  {
    id: 'height',
    title: 'Height',
    question: 'What is your height?',
    explanation: 'Proportion shapes everything—from trouser length to coat cut. We use your height to recommend silhouettes that flatter your frame.',
    type: 'height'
  },
  {
    id: 'weight',
    title: 'Weight',
    question: 'What is your weight?',
    explanation: 'Combined with height, this informs cuts that complement your frame. This information stays private and is never shared.',
    type: 'weight'
  },
  {
    id: 'style_direction',
    title: 'Styling Direction',
    question: 'Which styling direction should your recommendations prioritize?',
    explanation: 'This guides which visual concepts and products we show first. It\'s about how you like to dress — never about identity — and you can change it anytime in Settings.',
    type: 'single',
    options: [
      { value: 'menswear', label: 'Menswear / masculine styling' },
      { value: 'womenswear', label: 'Womenswear / feminine styling' },
      { value: 'androgynous', label: 'Androgynous / unisex styling' },
      { value: 'mixed', label: 'A mix of directions' },
      { value: 'no_preference', label: 'No preference' },
      { value: 'ask_each_time', label: 'Ask me depending on the outfit' },
    ]
  },
  {
    id: 'shopping_category_pref',
    title: 'Shopping Category',
    question: 'Which shopping category should product recommendations use first?',
    explanation: 'Many stores organise by department. Tell us where you prefer to shop first — we\'ll respect it across Shop, Search, and your AI Stylist.',
    type: 'single',
    options: [
      { value: 'men', label: 'Men\'s' },
      { value: 'women', label: 'Women\'s' },
      { value: 'unisex', label: 'Unisex' },
      { value: 'mixed', label: 'Mixed' },
      { value: 'no_preference', label: 'No preference' },
    ]
  },
  {
    id: 'style_presentations',
    title: 'Style Presentation',
    question: 'How do you want to dress?',
    explanation: 'Pick everything that resonates. We use this as a HARD FILTER on every recommendation — pieces outside your selection never appear in your feed. You can mix categories freely.',
    type: 'multiple',
    options: [
      { value: 'menswear', label: 'Menswear' },
      { value: 'womenswear', label: 'Womenswear' },
      { value: 'unisex', label: 'Unisex' },
      { value: 'masculine', label: 'Masculine-presenting' },
      { value: 'feminine', label: 'Feminine-presenting' },
      { value: 'androgynous', label: 'Androgynous' },
      { value: 'modestwear', label: 'Modestwear' },
      { value: 'streetwear', label: 'Streetwear' },
      { value: 'business_formal', label: 'Business / Formal' },
      { value: 'luxury_minimalist', label: 'Luxury / Minimalist' },
    ]
  },
  {
    id: 'lifestyle',
    title: 'Lifestyle',
    question: 'How would you describe your lifestyle?',
    explanation: 'Your daily life shapes what your wardrobe needs to deliver. A professional needs different staples than a creative or active life.',
    type: 'single',
    options: [
      { value: 'professional', label: 'Professional', desc: 'Office, meetings, networking' },
      { value: 'creative', label: 'Creative', desc: 'Flexible, artistic, freelance' },
      { value: 'casual', label: 'Casual', desc: 'Relaxed, everyday comfort' },
      { value: 'active', label: 'Active', desc: 'Sports, outdoor, dynamic' },
      { value: 'social', label: 'Social', desc: 'Events, dining, gatherings' }
    ]
  },
  {
    id: 'activities',
    title: 'Activities',
    question: 'What do you dress for?',
    explanation: 'We prioritise pieces that work for the moments that matter most to you. Pick everything that genuinely fills your calendar.',
    type: 'multiple',
    options: [
      { value: 'work', label: 'Work' },
      { value: 'social events', label: 'Social Events' },
      { value: 'date nights', label: 'Date Nights' },
      { value: 'travel', label: 'Travel' },
      { value: 'workouts', label: 'Workouts' },
      { value: 'errands', label: 'Errands' },
      { value: 'formal occasions', label: 'Formal Occasions' }
    ]
  },
  {
    id: 'budget',
    title: 'Budget',
    question: 'What is your typical clothing budget per item?',
    explanation: 'We align recommendations to investment levels that feel right for you—whether you favour clever high-street finds or considered luxury pieces.',
    type: 'single',
    options: [
      { value: 'budget', label: 'Budget-conscious', desc: 'Under $100 per piece' },
      { value: 'medium', label: 'Mid-range', desc: '$100 – $300 per piece' },
      { value: 'premium', label: 'Premium', desc: '$300 – $800 per piece' },
      { value: 'luxury', label: 'Luxury', desc: '$800+ per piece' }
    ]
  },
  {
    id: 'style_goals',
    title: 'Style Goals',
    question: 'What are your style goals?',
    explanation: 'These shape the aesthetic direction of your profile. Pick the words that describe how you want to look and feel in your clothes.',
    type: 'multiple',
    options: [
      { value: 'elegant', label: 'Elegant' },
      { value: 'sophisticated', label: 'Sophisticated' },
      { value: 'minimal', label: 'Minimal' },
      { value: 'romantic', label: 'Romantic' },
      { value: 'feminine', label: 'Feminine' },
      { value: 'edgy', label: 'Edgy' },
      { value: 'bold', label: 'Bold' },
      { value: 'classic', label: 'Classic' },
      { value: 'modern', label: 'Modern' }
    ]
  }
];

// ============ MAIN QUIZ COMPONENT ============
export const Quiz = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [phase, setPhase] = useState('core'); // 'core' | 'refine-intro' | 'refine'
  const [answers, setAnswers] = useState({
    gender: '',
    units: '',
    skin_tone: '',
    undertone: '',
    height: 170,
    weight: 70,
    lifestyle: '',
    activities: [],
    budget: '',
    style_goals: [],
    // optional
    heritage: '',
    cultural_notes_user: '',
    religious_events: [],
    modesty_preferences: [],
    top_size_system: '',
    bottom_size_system: '',
    shoe_size_system: '',
    favorite_colors_user: [],
    avoid_colors_user: [],
    fabric_avoids: [],
    style_presentations: [],
    style_direction: '',
    shopping_category_pref: '',
    avoid_categories: []
  });
  const [submitting, setSubmitting] = useState(false);

  // Refinement step definitions — built inline because they reference helper components
  const refineSteps = useMemo(() => ([
    {
      id: 'cultural',
      title: 'Cultural Style — Optional',
      question: 'Any cultural style preferences you\'d like us to honour?',
      explanation: 'Heritage often shapes our relationship with colour, silhouette, and ceremony. We use this only to enrich your recommendations—never to stereotype. Share whatever feels right, or skip this section entirely.'
    },
    {
      id: 'modesty',
      title: 'Modesty — Optional',
      question: 'Do you have coverage or modesty preferences?',
      explanation: 'We respect personal, religious, and cultural preferences around coverage. Select anything that applies, or skip if not relevant.'
    },
    {
      id: 'sizing',
      title: 'Sizing Systems — Optional',
      question: 'Which size systems do you typically buy in?',
      explanation: 'Tell us your preferred sizing per category—we\'ll surface labels and brands that align. Skip if you\'re still figuring this out.'
    },
    {
      id: 'fabric_avoids',
      title: 'Fabric Preferences — Optional',
      question: 'Are there any fabrics you actively avoid?',
      explanation: 'Ethical, religious, sensory, or personal—any fabric you\'d rather not wear. We\'ll exclude these from recommendations.'
    }
  ]), []);

  const totalCore = CORE_STEPS.length;
  const totalRefine = refineSteps.length;
  
  const isCorePhase = phase === 'core';
  const isRefineIntroPhase = phase === 'refine-intro';
  const isRefinePhase = phase === 'refine';

  const coreStep = isCorePhase ? CORE_STEPS[currentStep] : null;
  const refineStep = isRefinePhase ? refineSteps[currentStep] : null;

  const totalShownSteps = totalCore + 1 + totalRefine; // core + intro + refine
  const shownStepNumber = isCorePhase ? currentStep + 1 : isRefineIntroPhase ? totalCore + 1 : totalCore + 1 + currentStep + 1;
  const progress = (shownStepNumber / totalShownSteps) * 100;

  const handleSingleSelect = (field, value) => setAnswers({ ...answers, [field]: value });
  const handleMultiSelect = (field, value) => {
    const current = answers[field] || [];
    setAnswers({ ...answers, [field]: current.includes(value) ? current.filter(v => v !== value) : [...current, value] });
  };

  const canProceedCore = () => {
    const answer = answers[coreStep.id];
    if (coreStep.type === 'single') return !!answer;
    if (coreStep.type === 'multiple') return Array.isArray(answer) && answer.length > 0;
    if (coreStep.type === 'height') return answer >= 120 && answer <= 230;
    if (coreStep.type === 'weight') return answer >= 30 && answer <= 250;
    return false;
  };

  const submitQuiz = async () => {
    setSubmitting(true);
    try {
      // Clean up empty optional fields before submit
      const payload = { ...answers };
      Object.keys(payload).forEach(k => {
        if (payload[k] === '' || (Array.isArray(payload[k]) && payload[k].length === 0)) {
          if (!['gender', 'units', 'skin_tone', 'undertone', 'lifestyle', 'budget'].includes(k) &&
              !['height', 'weight'].includes(k) &&
              !['activities', 'style_goals', 'style_presentations'].includes(k)) {
            payload[k] = null;
          }
        }
      });
      await axios.post(`${window.__SG_API_BASE__}/api/quiz/submit`, payload, { withCredentials: true });
      navigate('/dashboard');
    } catch (error) {
      console.error('Quiz submission failed:', error);
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (isCorePhase) {
      if (currentStep < totalCore - 1) {
        setCurrentStep(currentStep + 1);
        window.scrollTo(0, 0);
      } else {
        // Done with core, show refinement intro
        setPhase('refine-intro');
        window.scrollTo(0, 0);
      }
    } else if (isRefineIntroPhase) {
      setPhase('refine');
      setCurrentStep(0);
      window.scrollTo(0, 0);
    } else if (isRefinePhase) {
      if (currentStep < totalRefine - 1) {
        setCurrentStep(currentStep + 1);
        window.scrollTo(0, 0);
      } else {
        submitQuiz();
      }
    }
  };

  const handleSkip = () => handleNext();

  const handleBack = () => {
    if (isRefinePhase) {
      if (currentStep > 0) { setCurrentStep(currentStep - 1); }
      else { setPhase('refine-intro'); }
      window.scrollTo(0, 0);
    } else if (isRefineIntroPhase) {
      setPhase('core');
      setCurrentStep(totalCore - 1);
      window.scrollTo(0, 0);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F1] flex flex-col">
      <div className="w-full h-1 bg-[#E5E4E2]">
        <div className="h-full bg-[#0D0D0D] transition-all duration-500" style={{ width: `${progress}%` }} data-testid="quiz-progress" />
      </div>

      <div className="flex-1 flex items-center px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-3xl mx-auto w-full">
          {/* ============== REFINEMENT INTRO ============== */}
          {isRefineIntroPhase && (
            <>
              <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#C7A96B] mb-4" data-testid="quiz-step-label">
                Optional Refinements
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl mb-6 leading-tight" data-testid="quiz-question">
                Want to refine your profile further?
              </h1>
              <div className="mb-10 pl-4 border-l-2 border-[#C7A96B]" data-testid="quiz-explanation">
                <p className="font-sans font-light text-[#8E8A84] italic leading-relaxed max-w-2xl">
                  We have everything we need to generate your style profile. If you'd like to share cultural preferences, modesty needs, sizing systems, or fabric considerations, the next four questions help us refine further. Skip any or all—your profile works beautifully either way.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                <button onClick={submitQuiz} disabled={submitting} data-testid="quiz-skip-all-refinement"
                  className="font-sans uppercase tracking-[0.2em] text-xs text-[#8E8A84] hover:text-[#0D0D0D] transition-colors px-6 py-4 border border-[#E5E4E2] rounded-none">
                  {submitting ? 'Generating...' : 'Skip & Generate Profile'}
                </button>
                <button onClick={handleNext} data-testid="quiz-start-refinement"
                  className="bg-[#0D0D0D] text-white hover:bg-black transition-colors rounded-none px-8 py-4 font-sans uppercase tracking-[0.2em] text-xs">
                  Continue refining →
                </button>
              </div>
            </>
          )}

          {/* ============== CORE STEPS ============== */}
          {isCorePhase && coreStep && (
            <>
              <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#8E8A84] mb-4" data-testid="quiz-step-label">
                Step {currentStep + 1} of {totalCore} — {coreStep.title}
              </p>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl mb-6 leading-tight" data-testid="quiz-question">
                {coreStep.question}
              </h1>
              <div className="mb-10 pl-4 border-l-2 border-[#C7A96B]" data-testid="quiz-explanation">
                <p className="font-sans font-light text-[#8E8A84] italic leading-relaxed max-w-2xl">
                  {coreStep.explanation}
                </p>
                {coreStep.type === 'multiple' && (
                  <p className="font-sans text-xs uppercase tracking-[0.2em] text-[#C7A96B] mt-3">
                    Select all that apply
                  </p>
                )}
              </div>

              {coreStep.type === 'single' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
                  {coreStep.options.map((opt) => (
                    <button key={opt.value} onClick={() => handleSingleSelect(coreStep.id, opt.value)}
                      data-testid={`quiz-option-${opt.value}`}
                      className={`text-left p-6 border-2 transition-all duration-300 rounded-2xl ${answers[coreStep.id] === opt.value ? 'border-[#0D0D0D] bg-white shadow-md' : 'border-[#E5E4E2] bg-[#F3F2F0] hover:border-[#C8BCA7]'}`}>
                      <div className="font-serif text-xl mb-1">{opt.label}</div>
                      {opt.desc && <div className="font-sans font-light text-sm text-[#8E8A84]">{opt.desc}</div>}
                    </button>
                  ))}
                </div>
              )}

              {coreStep.type === 'multiple' && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-12">
                  {coreStep.options.map((opt) => {
                    const selected = (answers[coreStep.id] || []).includes(opt.value);
                    return (
                      <button key={opt.value} onClick={() => handleMultiSelect(coreStep.id, opt.value)}
                        data-testid={`quiz-option-${opt.value}`}
                        className={`p-4 border-2 transition-all duration-300 rounded-2xl text-center ${selected ? 'border-[#0D0D0D] bg-white shadow-md' : 'border-[#E5E4E2] bg-[#F3F2F0] hover:border-[#C8BCA7]'}`}>
                        <div className="font-sans text-sm">{opt.label}</div>
                      </button>
                    );
                  })}
                </div>
              )}

              {coreStep.type === 'height' && (
                <HeightInput value={answers.height} units={answers.units || 'metric'} onChange={(v) => handleSingleSelect('height', v)} />
              )}
              {coreStep.type === 'weight' && (
                <WeightInput value={answers.weight} units={answers.units || 'metric'} onChange={(v) => handleSingleSelect('weight', v)} />
              )}

              <div className="flex items-center justify-between gap-4">
                <button onClick={handleBack} disabled={currentStep === 0} data-testid="quiz-back-button"
                  className="font-sans uppercase tracking-[0.2em] text-xs text-[#8E8A84] hover:text-[#0D0D0D] transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                  ← Back
                </button>
                <button onClick={handleNext} disabled={!canProceedCore()} data-testid="quiz-next-button"
                  className="bg-[#0D0D0D] text-white hover:bg-black transition-colors rounded-none px-8 py-4 font-sans uppercase tracking-[0.2em] text-xs disabled:opacity-30 disabled:cursor-not-allowed">
                  Continue →
                </button>
              </div>
            </>
          )}

          {/* ============== REFINEMENT STEPS ============== */}
          {isRefinePhase && refineStep && (
            <>
              <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#C7A96B] mb-4" data-testid="quiz-step-label">
                Refinement {currentStep + 1} of {totalRefine} — {refineStep.title}
              </p>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl mb-6 leading-tight" data-testid="quiz-question">
                {refineStep.question}
              </h1>
              <div className="mb-10 pl-4 border-l-2 border-[#C7A96B]" data-testid="quiz-explanation">
                <p className="font-sans font-light text-[#8E8A84] italic leading-relaxed max-w-2xl">
                  {refineStep.explanation}
                </p>
              </div>

              {refineStep.id === 'cultural' && (
                <div className="space-y-8 mb-12">
                  <div>
                    <label htmlFor="quiz-heritage-select" className="font-sans text-xs uppercase tracking-[0.2em] text-[#8E8A84] mb-3 block">
                      Cultural heritage influence (optional)
                    </label>
                    <select value={answers.heritage} onChange={(e) => handleSingleSelect('heritage', e.target.value)} id="quiz-heritage-select"
                      data-testid="quiz-heritage-select"
                      className="w-full border-b border-[#E5E4E2] bg-transparent focus:border-[#0D0D0D] focus:ring-0 rounded-none px-0 py-3 text-lg font-sans">
                      <option value="">— None / Skip —</option>
                      {HERITAGE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="quiz-cultural-text" className="font-sans text-xs uppercase tracking-[0.2em] text-[#8E8A84] mb-3 block">
                      Describe any cultural style preferences (optional)
                    </label>
                    <textarea value={answers.cultural_notes_user} onChange={(e) => handleSingleSelect('cultural_notes_user', e.target.value)} id="quiz-cultural-text"
                      data-testid="quiz-cultural-text"
                      placeholder="e.g. I love incorporating traditional embroidery, or I dress modestly for religious reasons..."
                      rows={3}
                      className="w-full border border-[#E5E4E2] bg-white focus:border-[#0D0D0D] focus:ring-0 rounded-2xl px-4 py-3 text-sm font-sans" />
                  </div>

                  <div>
                    <p className="font-sans text-xs uppercase tracking-[0.2em] text-[#8E8A84] mb-3 block">
                      Religious or cultural events you dress for (optional)
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {RELIGIOUS_EVENTS_OPTIONS.map(opt => {
                        const selected = answers.religious_events.includes(opt.value);
                        return (
                          <button key={opt.value} onClick={() => handleMultiSelect('religious_events', opt.value)}
                            data-testid={`quiz-religious-${opt.value}`}
                            className={`p-4 border-2 transition-all duration-300 rounded-2xl text-left text-sm font-sans ${selected ? 'border-[#0D0D0D] bg-white shadow-md' : 'border-[#E5E4E2] bg-[#F3F2F0] hover:border-[#C8BCA7]'}`}>
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {refineStep.id === 'modesty' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-12">
                  {MODESTY_OPTIONS.map(opt => {
                    const selected = answers.modesty_preferences.includes(opt.value);
                    return (
                      <button key={opt.value} onClick={() => handleMultiSelect('modesty_preferences', opt.value)}
                        data-testid={`quiz-modesty-${opt.value}`}
                        className={`p-4 border-2 transition-all duration-300 rounded-2xl text-left text-sm font-sans ${selected ? 'border-[#0D0D0D] bg-white shadow-md' : 'border-[#E5E4E2] bg-[#F3F2F0] hover:border-[#C8BCA7]'}`}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {refineStep.id === 'sizing' && (
                <div className="space-y-6 mb-12">
                  {[
                    { key: 'top_size_system', label: 'Tops & Outerwear' },
                    { key: 'bottom_size_system', label: 'Bottoms' },
                    { key: 'shoe_size_system', label: 'Shoes' }
                  ].map(cat => (
                    <div key={cat.key}>
                      <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#8E8A84] mb-3 block">
                        {cat.label}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {SIZE_SYSTEMS.map(sys => (
                          <button key={sys.value} onClick={() => handleSingleSelect(cat.key, answers[cat.key] === sys.value ? '' : sys.value)}
                            data-testid={`quiz-${cat.key}-${sys.value}`}
                            className={`px-5 py-2 border-2 rounded-full font-sans text-sm uppercase tracking-[0.15em] transition-all duration-300 ${answers[cat.key] === sys.value ? 'border-[#0D0D0D] bg-[#0D0D0D] text-white' : 'border-[#E5E4E2] bg-[#F3F2F0] hover:border-[#C8BCA7]'}`}>
                            {sys.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {refineStep.id === 'fabric_avoids' && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-12">
                  {FABRIC_AVOID_OPTIONS.map(opt => {
                    const selected = answers.fabric_avoids.includes(opt.value);
                    return (
                      <button key={opt.value} onClick={() => handleMultiSelect('fabric_avoids', opt.value)}
                        data-testid={`quiz-fabric-${opt.value}`}
                        className={`p-4 border-2 transition-all duration-300 rounded-2xl text-center ${selected ? 'border-[#0D0D0D] bg-white shadow-md' : 'border-[#E5E4E2] bg-[#F3F2F0] hover:border-[#C8BCA7]'}`}>
                        <div className="font-sans text-sm">{opt.label}</div>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                <button onClick={handleBack} data-testid="quiz-back-button"
                  className="font-sans uppercase tracking-[0.2em] text-xs text-[#8E8A84] hover:text-[#0D0D0D] transition-colors">
                  ← Back
                </button>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button onClick={handleSkip} disabled={submitting} data-testid="quiz-skip-button"
                    className="font-sans uppercase tracking-[0.2em] text-xs text-[#8E8A84] hover:text-[#0D0D0D] transition-colors px-6 py-4 border border-[#E5E4E2] rounded-none">
                    Skip this
                  </button>
                  <button onClick={handleNext} disabled={submitting} data-testid="quiz-next-button"
                    className="bg-[#0D0D0D] text-white hover:bg-black transition-colors rounded-none px-8 py-4 font-sans uppercase tracking-[0.2em] text-xs disabled:opacity-30 disabled:cursor-not-allowed">
                    {submitting ? 'Generating...' : currentStep === totalRefine - 1 ? 'Generate Profile' : 'Continue →'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
